import { useEffect, useMemo, useRef, useState } from "react";

import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { EditPromptsBanner } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/EditPromptsBanner";
import { AddedTopicsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupTopicsSection/AddedTopicsSection";
import { PromptsPerTopicSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupTopicsSection/PromptsPerTopicSection";
import { SuggestedTopicsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMSetupTopicsSection/SuggestedTopicsSection";
import { useFormStep } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useFormStep";
import { INITIAL_PROMPTS_WITH_TOPICS } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetupConst";
import { getMaxPrompts } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";

export const LLMSetupFormStep2 = () => {
    const {
        stepData: { suggestedTopics, selectedTopics, promptsPerTopic, isLoading },
        stepHandlers: {
            handlePromptPerTopicChange,
            handleTopicAdded,
            handleRemoveTopic,
            handleSuggestionReturned,
            handleSuggestionAccepted,
            openPromptsEditModal,
        },
        stepErrors,
        showStepErrors,
        dismissStepErrors,
    } = useFormStep();

    const topicError = useMemo<string>(() => {
        if (!showStepErrors) {
            return "";
        }
        return stepErrors.find((err) => err.field === "topic")?.message ?? "";
    }, [stepErrors, showStepErrors]);

    const [initialSuggestions, setInitialSuggestions] = useState<string[]>(suggestedTopics);
    const prevSelectedCountRef = useRef<number>(selectedTopics.length);
    const canTopicsBeAdded = selectedTopics.length < getMaxPrompts();
    const maxPromptsPerTopic = useMemo<number>(() => {
        const maxPrompts = getMaxPrompts();

        if (!selectedTopics?.length) {
            return maxPrompts;
        }

        return Math.floor(maxPrompts / selectedTopics.length);
    }, [selectedTopics]);

    const removeTopic = (topic: string): void => {
        handleRemoveTopic(topic);

        //this topic was pulled from the suggestions - return it
        if (initialSuggestions.includes(topic) && !suggestedTopics.includes(topic)) {
            handleSuggestionReturned(topic);
        }
    };

    useEffect(() => {
        const allSuggestionsIncluded = suggestedTopics.every((topic) => initialSuggestions.includes(topic));

        //this is a new suggestions list
        if (!allSuggestionsIncluded) {
            setInitialSuggestions([...suggestedTopics]);
        }
    }, [suggestedTopics]);

    useEffect(() => {
        const prevCount = prevSelectedCountRef.current;
        const currentCount = selectedTopics.length;

        if (prevCount === 0 && currentCount > 0 && promptsPerTopic === 0) {
            const initialValue = Math.min(INITIAL_PROMPTS_WITH_TOPICS, maxPromptsPerTopic);
            handlePromptPerTopicChange(initialValue);
        }

        prevSelectedCountRef.current = currentCount;
    }, [selectedTopics.length, promptsPerTopic, maxPromptsPerTopic, handlePromptPerTopicChange]);

    useEffect(() => {
        if (maxPromptsPerTopic < promptsPerTopic) {
            handlePromptPerTopicChange(maxPromptsPerTopic);
        }
    }, [promptsPerTopic, maxPromptsPerTopic, handlePromptPerTopicChange]);

    return (
        <Flex
            flexDirection="column"
            gap="24px"
        >
            <Stack gap="8px">
                <AddedTopicsSection
                    selectedTopics={selectedTopics}
                    onTopicAdded={handleTopicAdded}
                    onTopicRemove={removeTopic}
                    canTopicsBeAdded={canTopicsBeAdded}
                    error={topicError}
                    onUserInteraction={dismissStepErrors}
                />
                <SuggestedTopicsSection
                    suggestedTopics={suggestedTopics}
                    canTopicsBeAdded={canTopicsBeAdded}
                    isTopicLoading={isLoading}
                    onSuggestionAccepted={handleSuggestionAccepted}
                />
            </Stack>
            <PromptsPerTopicSection
                value={promptsPerTopic}
                selectedTopicsCount={selectedTopics?.length ?? 0}
                onChange={handlePromptPerTopicChange}
                isDisabled={!selectedTopics?.length}
                isLoading={isLoading}
            />
            <EditPromptsBanner
                onClick={openPromptsEditModal}
                isDisabled={isLoading}
            />
        </Flex>
    );
};
